// INSTRUCTIONS
// Add .track-click-logger to your element of choice and make sure this script is attached and loaded. (If not currently loaded with all other js in main-defer.entry.js)

// VARIABLES
/**
 * @typedef {Object} TrackingData
 * @property {number|null} userId - The ID of the user.
 * @property {string|null} sessionId - The session ID.
 * @property {number|null} establishmentId - The ID of the establishment.
 * @property {number|null} threadId - The ID of the thread.
 * @property {Date|null} visitedTime - The time the page was visited.
 * @property {Date|null} clickedTime - The time an item was clicked.
 * @property {Object|null} deviceInfo - Information about the device.
 * @property {boolean} loggedIn - Whether the user is logged in.
 * @property {Object|null} itemClicked - Information about the item clicked.
 * @property {string|null} currentPage - The current page URL.
 * @property {Object|null} searchParams - The search parameters.
 * @property {Object|null} queryParams - The query parameters.
 * @property {Object|null} cookieItems - The cookie items.
 * @property {Object|null} siteInfo - Information about the site.
 */

/**
 * General Tracking data
 * @type {TrackingData}
 */
let data = {
    userId: null,
    sessionId: null,
    establishmentId: null,
    threadId: null,
    visitedTime: null,
    clickedTime: null,
    deviceInfo: null,
    loggedIn: false,
    itemClicked: null,
    currentPage: null,
    searchParams: null,
    queryParams: null,
    cookieItems: null,
    siteInfo: null,
};


// UTILITY

/**
 * Decodes cookie and searches for matching key pair.
 * @param {string} name 
 * @returns Decoded cookie key pair value or null
 */
function getCookie(name) {
    let cookieArray = document.cookie.split(';');
    for(let i = 0; i < cookieArray.length; i++) {
        let cookiePair = cookieArray[i].split('=');
        if(name == cookiePair[0].trim()) {
            return decodeURIComponent(cookiePair[1]); 
        }
    }
    return null;
}

/**
 * Retrieves all cookie details from the document.
 * @returns {Object} An object containing all cookie details.
 */
function getAllCookieDetails() {
    const cookies = {};
    document.cookie.split(';').forEach(cookie => {
        const [name, value] = cookie.split('=').map(c => c.trim());
        cookies[name] = decodeURIComponent(value);
    });
    return cookies;
}

/**
 * Retrieves the thread ID from the URL path.
 * @returns {string|null} The thread ID if found, otherwise null.
 */
function getThreadID() {
        const url = new URL(window.location.href);
    
        const pathSegments = url.pathname.split('/').filter(segment => segment.trim() !== '');
        
        const bookingsIndex = pathSegments.indexOf('bookings') || pathSegments.indexOf('besprekings');
        
        // If 'bookings' is found and there is a segment after it, that segment is assumed to be the thread ID.
        if (bookingsIndex !== -1 && pathSegments.length > bookingsIndex + 1) {
            return pathSegments[bookingsIndex + 1];
        }
        
        return null;
}

/**
 * Retrieves the latest user ID from the local storage logs.
 * @returns {string|null} The latest user ID if found, otherwise null.
 */
function getLatestUserIdFromLocalStorage() {
    const logs = localStorage.getItem("logs-results-click");
    if (logs) {
        const entries = JSON.parse(logs);
        const sortedKeys = Object.keys(entries).sort();
        const latestKey = sortedKeys[sortedKeys.length - 1];
        if (latestKey && entries[latestKey].general && entries[latestKey].general.user_id) {
            return entries[latestKey].general.user_id;
        }
    }
    return null;
}

/**
 * Retrieves the user ID.
 * @returns {string|null} The user ID if available, or null if not.
 */
function getUserID() {
    const latestUserId = getLatestUserIdFromLocalStorage();
    if (latestUserId !== null) {
        return latestUserId;
    } else if (window.tg.customer_id === "") {
        return null;
    } else {
        return window.tg.customer_id;
    }
}

/**
 * Finds the data establishment ID from elements with the 'data-eid' attribute.
 * @returns {string|null} The data establishment ID, or null if not found.
 */
function findDataEstablishmentId() {
    const elementsWithDataEid = document.querySelectorAll('[data-eid]');
    const dataEidValues = Array.from(elementsWithDataEid).map(element => element.getAttribute('data-eid'));
    if (dataEidValues.length > 0) {
        const firstValue = dataEidValues[0];
        const intValue = parseInt(firstValue, 10);
        return isNaN(intValue) ? null : intValue;
    } else {
        return null;
    }
}

/**
 * Retrieves the description of an HTML element.
 * @param {HTMLElement} element - The HTML element to retrieve the description from.
 * @returns {string|null} The description of the element, or null if no description is found.
 */
function getElementDescription(element) {
    if (element.classList.contains('track-click-logger')) {
        return element.innerText.trim();
    }

    if (element.id) {
        const label = document.querySelector(`label[for="${element.id}"]`);
        if (label) return label.innerText;
    }

    const descriptiveChild = element.querySelector('span, div');
    if (descriptiveChild) return descriptiveChild.innerText;

    if (element.getAttribute('aria-label')) {
        return element.getAttribute('aria-label');
    }

    return null;
}

/**
 * Extracts search parameters from the current URL and returns them as an object.
 * @returns {Object} An object containing the search parameters.
 */
function extractQueryParams() {
    if (!window.location.search) {
        return null;
    }
    const searchParams = new URLSearchParams(window.location.search);
    const paramsObj = {};
    searchParams.forEach((value, key) => {
        paramsObj[key] = value;
    });
    return Object.keys(paramsObj).length > 0 ? paramsObj : null;
}


/**
 * Extracts the search parameters from the global `window.tg` object.
 * @returns {Object|null} The extracted search parameters or `null` if `window.tg` is not available.
 */
function getSearchParams() {
    if (!window.tg) {
        return null;
    }

    const {
        search_autocomplete_query,
        search_start,
        search_end,
        search_membership,
        search_guests_adt
    } = window.tg;

    return {
        searchAutocomplete: search_autocomplete_query,
        searchStart: search_start,
        searchEnd: search_end,
        searchMembership: search_membership,
        searchGuests: search_guests_adt
    }
}

/**
 * Retrieves site information from the global `window.tg` object.
 * @returns {Object|null} The site information object or null if `window.tg` is not defined.
 */
function getSiteInfo() {
    if (!window.tg) {
        return null;
    }
    
    const {
        site,
        site_domain,
        site_group,
        site_name,
        site_url
    } = window.tg;

    return {
        site,
        siteDomain: site_domain,
        siteGroup: site_group,
        siteName: site_name,
        siteURL: site_url
    }
}

/**
 * Retrieves device information including device type, operating system, and screen resolution.
 * @returns {Object} An object containing device information.
 * @property {string} deviceType - The type of device (either "Mobile" or "Desktop").
 * @property {string} os - The operating system of the device.
 * @property {string} resolution - The screen resolution of the device in the format "width x height".
 */
function getDeviceInfo() {
    const ua = navigator.userAgent;
    const screenWidth = window.screen.width;
    const screenHeight = window.screen.height;

    // Device Type
    const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(ua);
    const deviceType = isMobile ? 'Mobile' : 'Desktop';

    // Operating System
    let os = "Unknown OS";
    if (ua.indexOf("Win") != -1) os = "Windows";
    if (ua.indexOf("Mac") != -1) os = "MacOS";
    if (ua.indexOf("X11") != -1) os = "UNIX";
    if (ua.indexOf("Linux") != -1) os = "Linux";
    if (/Android/.test(ua)) os = "Android";
    if (/iPhone|iPad|iPod/.test(ua)) os = "iOS";

    let browser = "Unknown Browser";
    if (ua.indexOf("Firefox") != -1) browser = "Firefox";
    else if (ua.indexOf("Opera") != -1 || ua.indexOf("OPR") != -1) browser = "Opera";
    else if (ua.indexOf("Trident") != -1) browser = "Internet Explorer";
    else if (ua.indexOf("Edge") != -1) browser = "Edge";
    else if (ua.indexOf("Chrome") != -1) browser = "Chrome";
    else if (ua.indexOf("Safari") != -1) browser = "Safari";

    // Screen Resolution
    const resolution = `${screenWidth}x${screenHeight}`;

    return { deviceType, os, browser, resolution };
}

// MAIN FUNCTIONALITY

/**
 * Creates a log entry by sending session data to the server.
 */
function createLogEntry() {
    const sessionData = sessionStorage.getItem('logs-general-click');
    
    if (sessionData) {
        fetch('/ajax/general/click_tracker.php', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: sessionData, 
        })
        .then(response => response.json()) 
        .then(data => console.log('Success:', data))
        .catch(error => console.error('Error:', error)); 
    } else {
        console.log('No data in session storage to send.');
    }
}

/**
 * Initializes click tracking for elements matching the given selector.
 * @param {string} selector - The CSS selector for the elements to track clicks on.
 */
function initializeClickTracking(selector) {
    document.addEventListener('click', function(e) {
        // Use event delegation to check if the clicked element matches the selector
        // Attaching a single event listener to a common parent element and using it to catch events from its children, thanks to event bubbling.
        const targetElement = e.target.closest(selector);
        if (!targetElement) return; // Not the target element or its child, exit;

        let data = {
            userId: getUserID(),
            sessionId: getCookie('PHPSESSID'),
            establishmentId: findDataEstablishmentId(targetElement),
            threadId: getThreadID(),
            clickedTime: new Date().toISOString(),
            deviceInfo: getDeviceInfo(),
            loggedIn: getCookie('login_status') ? true : false,
            itemClicked: {
                element: targetElement.tagName,
                element_id: targetElement.id || null,
                element_data_target: targetElement.getAttribute('data-target') || null,
                label: getElementDescription(targetElement),
            },
            currentPage: window.location.pathname,
            queryParams: extractQueryParams(),
            searchParams: getSearchParams(),
            cookieItems: getAllCookieDetails(),
            siteInfo: getSiteInfo(),
        };

        sessionStorage.setItem('logs-general-click', JSON.stringify(data));
        createLogEntry();
    }, { passive: true }); // Mark the event listener as passive if not calling preventDefault
}
/**
 * When DOM is loaded do the following.
 */
document.addEventListener('DOMContentLoaded', () => {
    initializeClickTracking('.track-click-logger');
    data = {
        ...data,
        visitedTime: new Date().toISOString(),
    }
    sessionStorage.setItem('logs-general-click', JSON.stringify(data));
});